import React, { createContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'
import { IconMessagePlus } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import {
  focusChangeRequestInContent,
  setDrawerContentView,
  setFocusedChangeRequest,
} from '@/reducers/voiceAgents/voiceAgents.redux'
import { createChangeRequest } from '@/reducers/voiceAgents/voiceAgents.actions'
import CommentComposer from '@/components/comments/CommentComposer'
import { locateSnippet } from '@/components/comments/anchorSnippet'

import { TAB_LABELS } from '../drawerHelpers'
import { filterRequestsByTarget, isOpenRequest, TARGET_TYPE_BY_TAB } from '../changeRequestHelpers'
import { composeSegmentsText } from '../promptOffsets'
import ChangeRequestCard from './ChangeRequestCard'

// =============================================================================
// DrawerCommentsPanel — wraps a commentable drawer tab (prompt/tools/variables)
// with a content ↔ comments toggle. Comments are agent-wide change requests
// filtered to this node + tab's target; the blue dot marks open ones.
// =============================================================================

// What the panel hands the tab it wraps: a slot in the header row for the tab's
// own button. Null until the header has mounted.
export const DrawerTabContext = createContext({ actionsSlot: null })

const DrawerCommentsPanel = ({ node, tabKey, children }) => {
  const dispatch = useDispatch()
  const drawer = useSelector((state) => state.voiceAgents.drawer)
  const changeRequestItems = useSelector((state) => state.voiceAgents.changeRequests.items)
  // The wrapped tab's own header action (the prompt's Edit) portals in here, so
  // it sits on the toggle's row instead of claiming a second one below it.
  // State, not a ref: the child needs a render once the node exists.
  const [actionsSlot, setActionsSlot] = useState(null)
  const selectedAgentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const isCreating = useSelector((state) => state.voiceAgents.loading.createChangeRequest)
  const [composing, setComposing] = useState(false)

  const tabContext = useMemo(() => ({ actionsSlot }), [actionsSlot])

  const targetType = TARGET_TYPE_BY_TAB[tabKey]
  // The drawer only surfaces what still needs attention — resolved comments
  // stay reachable on the Change Requests page under its Resolved filter.
  const requests = filterRequestsByTarget(changeRequestItems, targetType, node.id).filter(
    isOpenRequest
  )
  const openCount = requests.length
  const showComments = drawer.contentView === 'comments'

  // A highlight comment is orphaned when its snippet no longer exists in the
  // node's current prompt text — flag it so the user knows why it has no mark.
  // Transcript comments are exempt: their snippet is what the agent *said*, and
  // a spoken line is never expected to appear verbatim in the prompt that
  // produced it (AVR-878).
  const { text: baseText } = composeSegmentsText(node.config?.segments, node.config?.promptText)
  const isOrphaned = (request) =>
    request.type !== 'transcript' &&
    Boolean(request.highlight?.snippet) &&
    isNil(locateSnippet(baseText, request.highlight.snippet, request.highlight.start))

  // The deep-link/page-click highlight is a transient "here's the comment you
  // came for" marker — any click outside the focused card clears it.
  useEffect(() => {
    if (isNil(drawer.focusedRequestId) || !showComments) {
      return undefined
    }
    const handlePointerDown = (event) => {
      if (!event.target.closest('.voice-agents__change-request-card--focused')) {
        dispatch(setFocusedChangeRequest(null))
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [dispatch, drawer.focusedRequestId, showComments])

  // A comment about the node as a whole is written right here: open the list,
  // type it, submit. It is not about any passage, so sending it over to the
  // prompt to be written there only put a box on top of the text it had nothing
  // to say about.
  const handleAddComment = () => {
    dispatch(setDrawerContentView('comments'))
    setComposing(true)
  }

  const handleSubmit = async (message) => {
    const result = await dispatch(
      createChangeRequest({ agentId: selectedAgentId, message, targetType, targetId: node.id })
    )
    if (result) {
      setComposing(false)
    }
  }

  return (
    <div className="voice-agents__drawer-comments" data-testid="drawer-comments-panel">
      <div className="voice-agents__drawer-comments-header">
        <SegmentedControl
          items={[
            {
              accessor: 'content',
              label: TAB_LABELS[tabKey],
              active: !showComments,
              onClick: () => dispatch(setDrawerContentView('content')),
              dataTestId: 'drawer-comments-toggle-content',
            },
            {
              accessor: 'comments',
              label: (
                <span className="flex-align-center small-gap">
                  Comments
                  {openCount > 0 && (
                    <Pill xs dataTestId="drawer-comments-count">
                      {openCount}
                    </Pill>
                  )}
                </span>
              ),
              active: showComments,
              onClick: () => dispatch(setDrawerContentView('comments')),
              dataTestId: 'drawer-comments-toggle-comments',
            },
          ]}
        />
        <span className="voice-agents__drawer-comments-actions">
          {/* Only on the Comments view, where it files a note about the node as
              a whole. On the content view the node's own affordance — the pill
              over the prompt text — is the way in, and two buttons for the one
              job read as two different jobs. */}
          {showComments && (
            <Button
              basic
              square
              small
              onClick={handleAddComment}
              aria-label="Add comment"
              data-testid="drawer-add-comment"
            >
              <IconMessagePlus />
            </Button>
          )}
          <span ref={setActionsSlot} />
        </span>
      </div>

      {showComments ? (
        <div className="voice-agents__drawer-comments-list" data-testid="drawer-comments-list">
          {composing && (
            <CommentComposer
              onSubmit={handleSubmit}
              onCancel={() => setComposing(false)}
              busy={isCreating}
            />
          )}
          {isEmpty(requests) && !composing && (
            <p className="voice-agents__drawer-tab-hint">No open comments for this tab.</p>
          )}
          {requests.map((request) => (
            <ChangeRequestCard
              key={request.id}
              request={request}
              focused={request.id === drawer.focusedRequestId}
              orphaned={isOrphaned(request)}
              showCallLink
              // Reading a comment and seeing the line it is about are the same
              // question, so go to the prompt with it focused — the display
              // scrolls its mark into view. On a prompt thousands of pixels
              // long, "somewhere above" is not an answer.
              onClick={() => dispatch(focusChangeRequestInContent(request.id))}
            />
          ))}
        </div>
      ) : (
        <DrawerTabContext.Provider value={tabContext}>{children}</DrawerTabContext.Provider>
      )}
    </div>
  )
}

export default DrawerCommentsPanel
